import { createSlice, type PayloadAction } from '@reduxjs/toolkit'

export interface AuthState {
    token: string | null
    user: {
        id: string
        email: string
        name: string
        isSuperAdmin?: boolean
    } | null
    /** The org this session is in. Login responses carry only id + role; name comes from GET /v2/orgs/current. */
    tenant: {
        id: string
        name?: string
        slug?: string
        role?: string
    } | null
    /** The org requires two-step verification and this account hasn't turned it on (API 403 MFA_REQUIRED). */
    mfaRequired?: boolean
    /** YOM staff suspended this session's org (API 403 ORG_SUSPENDED): the app shows why, not errors. */
    orgSuspended?: boolean
}

const TOKEN_KEY = 'yom_token'
const USER_KEY = 'yom_user'
const TENANT_KEY = 'yom_tenant'

/** A stored token; `"undefined"`/`"null"` (written by an old sign-up flow) count as none. */
export function storedToken(raw: string | null): string | null {
    return raw && raw !== 'undefined' && raw !== 'null' ? raw : null
}

function loadFromStorage(): AuthState {
    try {
        return {
            token: storedToken(localStorage.getItem(TOKEN_KEY)),
            user: JSON.parse(localStorage.getItem(USER_KEY) ?? 'null'),
            tenant: JSON.parse(localStorage.getItem(TENANT_KEY) ?? 'null'),
        }
    } catch {
        return { token: null, user: null, tenant: null }
    }
}

const initialState: AuthState = loadFromStorage()

const authSlice = createSlice({
    name: 'auth',
    initialState,
    reducers: {
        setCredentials(
            state,
            action: PayloadAction<{
                token: string
                user: AuthState['user']
                tenant: AuthState['tenant']
            }>,
        ) {
            state.token = action.payload.token
            state.user = action.payload.user
            state.tenant = action.payload.tenant
            // A new session (or another org) is re-checked by its first API answer.
            state.orgSuspended = false
            localStorage.setItem(TOKEN_KEY, action.payload.token)
            localStorage.setItem(USER_KEY, JSON.stringify(action.payload.user))
            localStorage.setItem(TENANT_KEY, JSON.stringify(action.payload.tenant))
        },
        setMfaRequired(state, action: PayloadAction<boolean>) {
            state.mfaRequired = action.payload
        },
        setOrgSuspended(state, action: PayloadAction<boolean>) {
            state.orgSuspended = action.payload
        },
        logout(state) {
            state.mfaRequired = false
            state.orgSuspended = false
            state.token = null
            state.user = null
            state.tenant = null
            localStorage.removeItem(TOKEN_KEY)
            localStorage.removeItem(USER_KEY)
            localStorage.removeItem(TENANT_KEY)
        },
    },
})

export const { setCredentials, logout, setMfaRequired, setOrgSuspended } = authSlice.actions

/** API answer meaning "turn on two-step verification first" (org policy). */
export function isMfaRequiredError(status: unknown, data: unknown): boolean {
    return (
        status === 403 &&
        (data as { error?: { code?: string } } | undefined)?.error?.code === 'MFA_REQUIRED'
    )
}

/** API answer meaning "this organisation is suspended" (runbook §D3; never for superadmins). */
export function isOrgSuspendedError(status: unknown, data: unknown): boolean {
    return (
        status === 403 &&
        (data as { error?: { code?: string } } | undefined)?.error?.code === 'ORG_SUSPENDED'
    )
}

/** Where a suspended org's members are sent (also shown in the API's 403 message). */
export const SUSPENSION_CONTACT_EMAIL = 'contact@aapastech.com'

export const authReducer = authSlice.reducer
